import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Check from '@mui/icons-material/Check';
import Lock from '@mui/icons-material/Lock';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { CONTENT_MAX_WIDTH, ctaSecondarySx } from '../../styles/tokens';
import { BORDER, DONE, INK, MUTED, MUTED_SOFT, SHADOW_CARD, SURFACE } from '../../pages/Practice/map/mapTokens';
import { PRICING_TEASER } from './homeContent';
import { SectionHeading, bandSx } from './homeUi';

/**
 * Three columns rather than the usual free/paid pair, because the product really
 * does have three modes — and the first one asks for nothing at all. Leading with
 * Guest removes the "is this going to make me sign up?" objection before it forms.
 */
const PricingTeaser = ({ tone }) => (
  <Box component="section" sx={bandSx(tone)}>
    <SectionHeading title={PRICING_TEASER.title} subtitle={PRICING_TEASER.subtitle} />

    <Box
      sx={{
        display: 'grid',
        gap: { xs: 2.5, md: 3 },
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        alignItems: 'stretch',
        maxWidth: CONTENT_MAX_WIDTH,
        mx: 'auto',
      }}
    >
      {PRICING_TEASER.tiers.map((tier) => (
        <Box
          key={tier.key}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 2.5, md: 3 },
            bgcolor: SURFACE,
            border: tier.highlight ? '2px solid' : `1px solid ${BORDER}`,
            borderColor: tier.highlight ? 'primary.main' : BORDER,
            borderRadius: '16px',
            boxShadow: tier.highlight ? SHADOW_CARD : 'none',
          }}
        >
          <Typography variant="h6" component="h3" sx={{ color: 'primary.main', fontWeight: 800 }}>
            {tier.name}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5, mb: 2 }}>
            <Typography component="span" sx={{ color: 'primary.main', fontWeight: 900, fontSize: '1.75rem' }}>
              {tier.price}
            </Typography>
            <Typography component="span" sx={{ color: MUTED, fontSize: '0.875rem', fontWeight: 600 }}>
              {tier.note}
            </Typography>
          </Box>

          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1, flexGrow: 1 }}>
            {tier.features.map((feature) => (
              <Box component="li" key={feature} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Check aria-hidden sx={{ fontSize: 18, color: DONE, mt: '2px', flex: '0 0 auto' }} />
                <Typography sx={{ color: INK, fontSize: '0.9375rem', lineHeight: 1.6 }}>{feature}</Typography>
              </Box>
            ))}
            {tier.excluded.map((feature) => (
              <Box component="li" key={feature} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Lock aria-hidden sx={{ fontSize: 18, color: MUTED_SOFT, mt: '2px', flex: '0 0 auto' }} />
                <Typography sx={{ color: MUTED_SOFT, fontSize: '0.9375rem', lineHeight: 1.6 }}>{feature}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>

    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        gap: 2,
        mt: 4,
        maxWidth: CONTENT_MAX_WIDTH,
        mx: 'auto',
      }}
    >
      <Button
        component={RouterLink}
        to="/pricing"
        variant="outlined"
        size="large"
        endIcon={<ArrowForward />}
        sx={ctaSecondarySx}
      >
        {PRICING_TEASER.cta}
      </Button>
      <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>{PRICING_TEASER.note}</Typography>
    </Box>

    <Typography
      sx={{
        display: { xs: 'none', md: 'block' },
        color: MUTED,
        fontSize: '0.9375rem',
        lineHeight: 1.7,
        maxWidth: CONTENT_MAX_WIDTH,
        mx: 'auto',
        mt: 3,
      }}
    >
      {PRICING_TEASER.supportingNote}
    </Typography>
  </Box>
);

export default PricingTeaser;
